import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch, pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { map, tap } from 'rxjs';

import { NotificationService } from '@core/shared/util';
import { isMaintenanceLockError } from '@mp/system/maintenance/data-access';

import { FeedbackService } from '../services';

import { FeedbackActions } from './feedback.actions';

/**
 * Der globale HTTP-Interceptor unterdrückt für einen so markierten 503 nur seine EIGENE, generische
 * Fehlermeldung (`HttpErrorClaimHandler`) - dieser Effekt zeigt seine Meldung unabhängig davon selbst an
 * und muss deshalb selbst prüfen. Sonst zeigt die App bei jeder aktiven, nicht befreiten Wartungssperre
 * zusätzlich zur Sperrmeldung noch einen irreführenden "Feedback konnte nicht geladen werden"-Toast,
 * obwohl es sich um exakt dieselbe, bereits angezeigte Sperre handelt.
 */
@Injectable()
export class FeedbackEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(FeedbackService);
  private readonly notificationService = inject(NotificationService);

  fetchFeedbackSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FeedbackActions.fetchFeedbackSettings),
      fetch({
        run: () => {
          return this.service
            .getFeedbackSettings()
            .pipe(map((feedbackSettings) => FeedbackActions.fetchFeedbackSettingsSuccess({ feedbackSettings })));
        },
        onError: (_action, error: unknown) => {
          if (!isMaintenanceLockError(error)) {
            this.notificationService.toastDanger('Beim Laden der Feedback Einstellungen ist ein Fehler aufgetreten.');
          }

          return FeedbackActions.fetchFeedbackSettingsError();
        },
      }),
    );
  });

  updateFeedbackSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FeedbackActions.updateFeedbackSettings),
      pessimisticUpdate({
        run: ({ feedbackSettings }) => {
          return this.service.updateFeedbackSettings(feedbackSettings).pipe(
            tap(() => this.notificationService.toastSuccess('Die Feedback Einstellungen wurden aktualisiert.')),
            map((feedbackSettings) => FeedbackActions.updateFeedbackSettingsSuccess({ feedbackSettings })),
          );
        },
        onError: (_action, error: unknown) => {
          if (!isMaintenanceLockError(error)) {
            this.notificationService.toastDanger(
              'Beim Aktualisieren der Feedback Einstellungen ist ein Fehler aufgetreten.',
            );
          }

          return FeedbackActions.updateFeedbackSettingsError();
        },
      }),
    );
  });
}
